import React, { useEffect, useState, useContext } from 'react';
import {
  Typography, Table, Button, Modal, Form, Input, Select, Space, message, Tag, Switch,
} from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import api from '../api';
import WeeklyHoursEditor, { hoursToForm, formToHours, hoursSummary } from '../components/WeeklyHoursEditor';
import { UserContext } from '../App';

export default function TeamsAdminPage() {
  const currentUser = useContext(UserContext);
  const isAdmin     = currentUser?.Role === 'admin';
  const [teams, setTeams]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem]   = useState(null);
  const [saving, setSaving]       = useState(false);
  const [form] = Form.useForm();
  // Paycor shift department names seen on the schedule, for the picker.
  const [deptOptions, setDeptOptions] = useState([]);

  const load = () => {
    setLoading(true);
    api.get('/api/admin/teams')
      .then((r) => setTeams(r.data))
      .catch(() => message.error('Failed to load teams.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!modalOpen) return;
    api.get('/api/admin/teams/shift-department-options')
      .then((r) => setDeptOptions((r.data || []).map((n) => ({ value: n, label: n }))))
      .catch(() => setDeptOptions([]));
  }, [modalOpen]);

  const openCreate = () => {
    setEditItem(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditItem(record);
    form.setFieldsValue({
      name: record.Name,
      external_id: record.ExternalID || '',
      external_department_code: record.ExternalDepartmentCode || '',
      status: record.Status,
      include_in_rankings: !!record.IncludeInRankings,
      sync_agent_monthly_stats: !!record.SyncAgentMonthlyStats,
      hide_ccaas_status: !!record.HideCCaasStatus,
      hide_upsell: !!record.HideUpsell,
      has_client_users: !!record.HasClientUsers,
      is_client_team: !!record.IsClientTeam,
      non_oncall: hoursToForm(record.NonOnCallHours),
      oncall_message: record.OnCallMessage || '',
      shift_departments: record.ShiftDepartments || [],
    });
    setModalOpen(true);
  };

  const toggleRankings = async (record, checked) => {
    try {
      await api.put(`/api/admin/teams/${record.ID}`, { include_in_rankings: checked });
      setTeams((prev) => prev.map((t) => t.ID === record.ID ? { ...t, IncludeInRankings: checked ? 1 : 0 } : t));
    } catch {
      message.error('Failed to update team.');
    }
  };

  const toggleSync = async (record, checked) => {
    try {
      await api.put(`/api/admin/teams/${record.ID}`, { sync_agent_monthly_stats: checked });
      setTeams((prev) => prev.map((t) => t.ID === record.ID ? { ...t, SyncAgentMonthlyStats: checked ? 1 : 0 } : t));
    } catch {
      message.error('Failed to update team.');
    }
  };

  const toggleHideCCaas = async (record, checked) => {
    try {
      await api.put(`/api/admin/teams/${record.ID}`, { hide_ccaas_status: checked });
      setTeams((prev) => prev.map((t) => t.ID === record.ID ? { ...t, HideCCaasStatus: checked ? 1 : 0 } : t));
    } catch {
      message.error('Failed to update team.');
    }
  };

  const toggleIsClientTeam = async (record, checked) => {
    try {
      await api.put(`/api/admin/teams/${record.ID}`, { is_client_team: checked });
      setTeams((prev) => prev.map((t) => t.ID === record.ID ? { ...t, IsClientTeam: checked ? 1 : 0 } : t));
    } catch {
      message.error('Failed to update team.');
    }
  };

  const toggleHasClientUsers = async (record, checked) => {
    try {
      await api.put(`/api/admin/teams/${record.ID}`, { has_client_users: checked });
      setTeams((prev) => prev.map((t) => t.ID === record.ID ? { ...t, HasClientUsers: checked ? 1 : 0 } : t));
    } catch {
      message.error('Failed to update team.');
    }
  };

  const toggleHideUpsell = async (record, checked) => {
    try {
      await api.put(`/api/admin/teams/${record.ID}`, { hide_upsell: checked });
      setTeams((prev) => prev.map((t) => t.ID === record.ID ? { ...t, HideUpsell: checked ? 1 : 0 } : t));
    } catch {
      message.error('Failed to update team.');
    }
  };

  const handleSave = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    const { hours: nonOnCall, error: hoursError } = formToHours(values.non_oncall);
    if (hoursError) { message.error(hoursError); return; }
    setSaving(true);
    try {
      if (editItem) {
        await api.put(`/api/admin/teams/${editItem.ID}`, {
          name: values.name,
          external_id: values.external_id || null,
          external_department_code: values.external_department_code || null,
          status: values.status,
          include_in_rankings: values.include_in_rankings,
          sync_agent_monthly_stats: values.sync_agent_monthly_stats,
          hide_ccaas_status: values.hide_ccaas_status,
          hide_upsell: values.hide_upsell,
          has_client_users: values.has_client_users,
          is_client_team: values.is_client_team,
          non_oncall_hours: nonOnCall,
          oncall_message: values.oncall_message || '',
          shift_departments: values.shift_departments || [],
        });
        message.success('Team updated.');
      } else {
        const res = await api.post('/api/admin/teams', {
          name: values.name,
          external_id: values.external_id || null,
          external_department_code: values.external_department_code || null,
        });
        if (nonOnCall || values.oncall_message || values.shift_departments?.length) {
          await api.put(`/api/admin/teams/${res.data.id}`, {
            non_oncall_hours: nonOnCall, oncall_message: values.oncall_message || '',
            shift_departments: values.shift_departments || [],
          });
        }
        message.success('Team created.');
      }
      setModalOpen(false);
      load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to save team.');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'ID', key: 'ID', width: 60 },
    {
      title: 'Team Name',
      dataIndex: 'Name',
      key: 'Name',
      sorter: (a, b) => a.Name.localeCompare(b.Name),
    },
    {
      title: 'External ID',
      dataIndex: 'ExternalID',
      key: 'ExternalID',
      width: 120,
      render: (v) => v || <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: 'External Dept Code',
      dataIndex: 'ExternalDepartmentCode',
      key: 'ExternalDepartmentCode',
      width: 110,
      render: (v) => v || <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: 'Shift Departments',
      dataIndex: 'ShiftDepartments',
      key: 'ShiftDepartments',
      width: 200,
      render: (v) => ((v || []).length
        ? <Space size={[0, 4]} wrap>{v.map((n) => <Tag key={n}>{n}</Tag>)}</Space>
        : <Typography.Text type="secondary">—</Typography.Text>),
    },
    {
      title: 'Status',
      dataIndex: 'Status',
      key: 'Status',
      width: 110,
      render: (v) => <Tag color={v === 'active' ? 'green' : 'red'}>{v === 'active' ? 'Active' : 'Inactive'}</Tag>,
    },
    {
      title: 'In Rankings',
      dataIndex: 'IncludeInRankings',
      key: 'IncludeInRankings',
      width: 110,
      render: (v, record) => (
        <Switch
          size="small"
          checked={!!v}
          disabled={!isAdmin}
          onChange={isAdmin ? (checked) => toggleRankings(record, checked) : undefined}
        />
      ),
    },
    {
      title: 'Sync Stats',
      dataIndex: 'SyncAgentMonthlyStats',
      key: 'SyncAgentMonthlyStats',
      width: 100,
      render: (v, record) => (
        <Switch
          size="small"
          checked={!!v}
          disabled={!isAdmin}
          onChange={isAdmin ? (checked) => toggleSync(record, checked) : undefined}
        />
      ),
    },
    {
      title: 'Hide CCaaS',
      dataIndex: 'HideCCaasStatus',
      key: 'HideCCaasStatus',
      width: 110,
      render: (v, record) => (
        <Switch
          size="small"
          checked={!!v}
          disabled={!isAdmin}
          onChange={isAdmin ? (checked) => toggleHideCCaas(record, checked) : undefined}
        />
      ),
    },
    {
      title: 'Hide Upsell',
      dataIndex: 'HideUpsell',
      key: 'HideUpsell',
      width: 110,
      render: (v, record) => (
        <Switch
          size="small"
          checked={!!v}
          disabled={!isAdmin}
          onChange={isAdmin ? (checked) => toggleHideUpsell(record, checked) : undefined}
        />
      ),
    },
    // An internal team serving a client (Cost vs Revenue: direct cost,
    // absorbs back-office cost).
    {
      title: 'Is Client Team',
      dataIndex: 'IsClientTeam',
      key: 'IsClientTeam',
      width: 120,
      render: (v, record) => (
        <Switch
          size="small"
          checked={!!v}
          disabled={!isAdmin}
          onChange={isAdmin ? (checked) => toggleIsClientTeam(record, checked) : undefined}
        />
      ),
    },
    // Holds client users (clients' own logins): left out of the internal
    // pages' Team dropdowns. Not IsClientTeam (Cost vs Revenue).
    {
      title: 'Has Client Users',
      dataIndex: 'HasClientUsers',
      key: 'HasClientUsers',
      width: 130,
      render: (v, record) => (
        <Switch
          size="small"
          checked={!!v}
          disabled={!isAdmin}
          onChange={isAdmin ? (checked) => toggleHasClientUsers(record, checked) : undefined}
        />
      ),
    },
    {
      title: 'Non On-Call Hours',
      key: 'NonOnCallHours',
      width: 220,
      render: (_, r) => {
        const parts = hoursSummary(r.NonOnCallHours);
        return parts ? parts.map((x) => <div key={x} style={{ fontSize: 12 }}>{x}</div>) : '—';
      },
    },
    ...(isAdmin ? [{
      title: '',
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>
          Edit
        </Button>
      ),
    }] : []),
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>Team Management</Typography.Title>
        {isAdmin && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Add Team
          </Button>
        )}
      </div>

      <Table
        rowKey="ID"
        dataSource={teams}
        columns={columns}
        loading={loading}
        size="small"
        pagination={{ defaultPageSize: 25, showTotal: (t) => `${t} teams` }}
      />

      <Modal maskClosable={false}
        title={editItem ? `Edit — ${editItem.Name}` : 'New Team'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        confirmLoading={saving}
        okText="Save"
        width={560}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            label="Team Name"
            name="name"
            rules={[{ required: true, message: 'Name is required' }]}
          >
            <Input placeholder="e.g. BGE" />
          </Form.Item>
          <Form.Item
            label="External ID"
            name="external_id"
            extra="The team_no from superior.teams used by the sync script."
          >
            <Input placeholder="e.g. 801698" />
          </Form.Item>
          <Form.Item
            label="External Dept Code"
            name="external_department_code"
            extra="Paycor department code — users with this dept code will be auto-assigned to this team."
          >
            <Input placeholder="e.g. 20401" />
          </Form.Item>
          {/* Paycor shift departments of the team: who sees them on
              Oversight > Scheduled Staff By Hour. Tags, so a name not yet on a
              shift can be entered too. */}
          <Form.Item label="Shift Departments" name="shift_departments">
            <Select mode="tags" allowClear options={deptOptions} tokenSeparators={[]} />
          </Form.Item>
          <Form.Item label="Non On-Call Hours (Eastern)" style={{ marginBottom: 8 }}>
            <WeeklyHoursEditor name="non_oncall" />
          </Form.Item>
          {/* Blank shows the standard message (the placeholder). */}
          <Form.Item label="On-Call Message" name="oncall_message">
            <Input.TextArea rows={3} maxLength={2000} showCount
              placeholder="This absence is during the on-call window. Please follow the instructions to alert the on-call person." />
          </Form.Item>
          {editItem && (
            <Form.Item label="Status" name="status">
              <Select
                options={[
                  { label: 'Active',   value: 'active'   },
                  { label: 'Inactive', value: 'inactive' },
                ]}
              />
            </Form.Item>
          )}
          <Form.Item
            label="Include in Rankings"
            name="include_in_rankings"
            valuePropName="checked"
            extra="When off, agents on this team are excluded from percentile peer groups and the Rankings Board."
          >
            <Switch />
          </Form.Item>
          <Form.Item
            label="Sync Agent Monthly Stats"
            name="sync_agent_monthly_stats"
            valuePropName="checked"
            extra="When off, agents on this team are skipped by the nightly stats sync (use for IT, management, etc.)."
          >
            <Switch />
          </Form.Item>
          <Form.Item
            label="Hide CCaaS Status"
            name="hide_ccaas_status"
            valuePropName="checked"
            extra="When on, the CCaaS Status tab is hidden for agents on this team (use for teams that don't use CCaaS)."
          >
            <Switch />
          </Form.Item>
          <Form.Item
            label="Hide Upsell"
            name="hide_upsell"
            valuePropName="checked"
            extra="When on, the Upsells tab is hidden for agents on this team (use for teams that don't track upsells)."
          >
            <Switch />
          </Form.Item>
          <Form.Item label="Is Client Team" name="is_client_team" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item label="Has Client Users" name="has_client_users" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
